/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Document Management DTOs & Validation Schemas (OpenAPI 3.1 Compatible)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

/**
 * Supported document content types.
 */
export const DocumentTypeEnum = z
  .enum(['article', 'code', 'wizard', 'custom'])
  .openapi({
    description: 'Content archetype for the document',
    example: 'article',
  });

export type DocumentType = z.infer<typeof DocumentTypeEnum>;

/**
 * Supported export formats.
 */
export const DocumentExportFormatEnum = z
  .enum(['markdown', 'html', 'txt', 'json'])
  .openapi({
    description: 'Target export file format',
    example: 'markdown',
  });

export type DocumentExportFormat = z.infer<typeof DocumentExportFormatEnum>;

/**
 * Full Document entity schema.
 */
export const DocumentItemSchema = z
  .object({
    id: z.string().uuid().openapi({
      description: 'Unique document UUID identifier',
      example: 'd123e456-e89b-12d3-a456-426614174000',
    }),
    workspaceId: z.string().uuid().openapi({
      description: 'Owning workspace UUID',
      example: '123e4567-e89b-12d3-a456-426614174000',
    }),
    folderId: z.string().uuid().nullable().optional().openapi({
      description: 'Optional parent folder UUID',
      example: 'fol_123456789',
    }),
    userId: z.string().openapi({
      description: 'Author user ID',
      example: 'usr_writer_1',
    }),
    title: z.string().openapi({
      description: 'Document title',
      example: 'Getting Started with Edge AI on Cloudflare Workers',
    }),
    slug: z.string().openapi({
      description: 'URL-friendly unique slug',
      example: 'getting-started-with-edge-ai-on-cloudflare-workers',
    }),
    content: z.string().openapi({
      description: 'Complete document markdown/HTML content',
      example: '# Getting Started\n\nCloudflare Workers allow sub-millisecond execution...',
    }),
    type: DocumentTypeEnum,
    templateId: z.string().nullable().optional().openapi({
      description: 'Optional origin prompt template ID',
      example: 'tpl_blog_post',
    }),
    tokensUsed: z.number().int().openapi({
      description: 'Cumulative LLM tokens used to generate this document',
      example: 1250,
    }),
    creditsCost: z.number().openapi({
      description: 'Platform credit units deducted for generating this document',
      example: 1.5,
    }),
    wordCount: z.number().int().openapi({
      description: 'Calculated total word count',
      example: 850,
    }),
    createdAt: z.string().datetime().or(z.date()).openapi({
      description: 'Timestamp when document was created',
    }),
    updatedAt: z.string().datetime().or(z.date()).openapi({
      description: 'Timestamp when document was last modified',
    }),
  })
  .openapi('DocumentItem');

export type DocumentItem = z.infer<typeof DocumentItemSchema>;

/**
 * Summary schema for listing documents with pagination.
 */
export const DocumentSummarySchema = z
  .object({
    id: z.string().uuid().openapi({
      description: 'Unique document UUID identifier',
      example: 'd123e456-e89b-12d3-a456-426614174000',
    }),
    workspaceId: z.string().uuid().openapi({
      description: 'Owning workspace UUID',
      example: '123e4567-e89b-12d3-a456-426614174000',
    }),
    folderId: z.string().uuid().nullable().optional().openapi({
      description: 'Optional parent folder UUID',
      example: 'fol_123456789',
    }),
    userId: z.string().openapi({
      description: 'Author user ID',
      example: 'usr_writer_1',
    }),
    title: z.string().openapi({
      description: 'Document title',
      example: 'Getting Started with Edge AI on Cloudflare Workers',
    }),
    slug: z.string().openapi({
      description: 'URL-friendly unique slug',
      example: 'getting-started-with-edge-ai-on-cloudflare-workers',
    }),
    type: DocumentTypeEnum,
    tokensUsed: z.number().int().openapi({
      description: 'Cumulative LLM tokens used',
      example: 1250,
    }),
    creditsCost: z.number().openapi({
      description: 'Platform credits deducted',
      example: 1.5,
    }),
    wordCount: z.number().int().openapi({
      description: 'Calculated word count',
      example: 850,
    }),
    excerpt: z.string().openapi({
      description: 'Short content preview (first 150 characters)',
      example: 'Cloudflare Workers allow sub-millisecond execution without cold starts...',
    }),
    createdAt: z.string().datetime().or(z.date()).openapi({
      description: 'Timestamp when document was created',
    }),
    updatedAt: z.string().datetime().or(z.date()).openapi({
      description: 'Timestamp when document was last modified',
    }),
  })
  .openapi('DocumentSummary');

export type DocumentSummary = z.infer<typeof DocumentSummarySchema>;

/**
 * Query schema for searching, filtering, and paginating documents.
 */
export const DocumentQuerySchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1).openapi({
      description: 'Page number for pagination',
      example: 1,
    }),
    limit: z.coerce.number().int().min(1).max(100).default(20).openapi({
      description: 'Number of items per page (max 100)',
      example: 20,
    }),
    search: z.string().max(100).optional().openapi({
      description: 'Search keyword matching document title or content',
      example: 'Cloudflare',
    }),
    folderId: z.string().uuid().optional().openapi({
      description: 'Filter by folder UUID',
      example: 'fol_123456789',
    }),
    type: DocumentTypeEnum.optional().openapi({
      description: 'Filter by document archetype',
      example: 'article',
    }),
    sortBy: z
      .enum(['createdAt', 'updatedAt', 'title', 'wordCount'])
      .default('createdAt')
      .openapi({
        description: 'Sort field',
        example: 'createdAt',
      }),
    sortOrder: z
      .enum(['asc', 'desc'])
      .default('desc')
      .openapi({
        description: 'Sort ordering direction',
        example: 'desc',
      }),
  })
  .openapi('DocumentQuery');

export type DocumentQuery = z.infer<typeof DocumentQuerySchema>;

/**
 * Request schema for creating a new document.
 */
export const CreateDocumentRequestSchema = z
  .object({
    title: z.string().min(1).max(250).openapi({
      description: 'Document title',
      example: 'Architecture of Modern Edge Applications',
    }),
    content: z.string().default('').openapi({
      description: 'Initial document text or markdown content',
      example: '# Architecture\n\nThis guide explores edge computing.',
    }),
    folderId: z.string().uuid().nullable().optional().openapi({
      description: 'Optional folder UUID where the document belongs',
      example: 'fol_123456789',
    }),
    type: DocumentTypeEnum.default('article'),
    templateId: z.string().optional().openapi({
      description: 'Optional prompt template ID used to generate this',
      example: 'tpl_blog_post',
    }),
  })
  .openapi('CreateDocumentRequest');

export type CreateDocumentRequest = z.infer<typeof CreateDocumentRequestSchema>;

/**
 * Request schema for partially updating an existing document.
 */
export const UpdateDocumentRequestSchema = z
  .object({
    title: z.string().min(1).max(250).optional().openapi({
      description: 'Updated document title',
      example: 'Advanced Architecture of Modern Edge Applications',
    }),
    content: z.string().optional().openapi({
      description: 'Updated document markdown or text content',
      example: '# Advanced Architecture\n\nUpdated contents...',
    }),
    folderId: z.string().uuid().nullable().optional().openapi({
      description: 'Move document to folder UUID or set null for root',
      example: 'fol_123456789',
    }),
    type: DocumentTypeEnum.optional(),
  })
  .openapi('UpdateDocumentRequest');

export type UpdateDocumentRequest = z.infer<typeof UpdateDocumentRequestSchema>;

/**
 * Export document query schema.
 */
export const DocumentExportQuerySchema = z
  .object({
    format: DocumentExportFormatEnum.default('markdown').openapi({
      description: 'Target export file format: markdown, html, txt, or json',
      example: 'markdown',
    }),
  })
  .openapi('DocumentExportQuery');

export type DocumentExportQuery = z.infer<typeof DocumentExportQuerySchema>;
